import React from "react";

/**
 * Anime AI Robot character — center-hero variant.
 * Now a friendlier AI-robot: antenna with pulsing light, chest arc-reactor core,
 * violet visor eyes, sleek headphones. Designed for center placement in hero.
 */
export const AnimeCharacter = () => {
  return (
    <div
      data-testid="anime-character"
      className="relative w-[300px] sm:w-[360px] md:w-[420px] mx-auto floaty"
      aria-label="AI Robot anime character"
    >
      {/* halo */}
      <div className="absolute inset-0 -z-10 blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(168,85,247,0.45), transparent 55%), radial-gradient(circle at 60% 70%, rgba(236,72,153,0.30), transparent 55%)",
        }}
      />

      <svg viewBox="0 0 420 540" className="w-full h-auto drop-shadow-[0_20px_60px_rgba(168,85,247,0.35)]">
        <defs>
          <linearGradient id="hairGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#1a1030" />
            <stop offset="1" stopColor="#2a1747" />
          </linearGradient>
          <linearGradient id="hairHi" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#C084FC" />
            <stop offset="1" stopColor="#F0ABFC" />
          </linearGradient>
          <linearGradient id="jacketGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1b0d33" />
            <stop offset="1" stopColor="#0e0620" />
          </linearGradient>
          <linearGradient id="skin" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f8d5bd" />
            <stop offset="1" stopColor="#e5b48f" />
          </linearGradient>
          <radialGradient id="visorGrad" cx="0.5" cy="0.4" r="0.7">
            <stop offset="0" stopColor="#F0ABFC" stopOpacity="1" />
            <stop offset="1" stopColor="#2a0a3f" stopOpacity="1" />
          </radialGradient>
          <radialGradient id="coreGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#FDE68A" />
            <stop offset="0.6" stopColor="#F59E0B" stopOpacity="0.9" />
            <stop offset="1" stopColor="#A855F7" stopOpacity="0" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Antenna */}
        <line x1="210" y1="90" x2="210" y2="55" stroke="#C084FC" strokeWidth="3" strokeLinecap="round" filter="url(#glow)" />
        <circle cx="210" cy="48" r="7" fill="#FDE68A" filter="url(#glow)" className="antenna-dot" />

        {/* Body / jacket */}
        <g>
          <path d="M70 500 Q110 380 210 370 Q310 380 350 500 Z" fill="url(#jacketGrad)" stroke="rgba(168,85,247,0.45)" strokeWidth="1.4" />
          {/* collar */}
          <path d="M160 380 L210 420 L260 380 L255 440 L210 465 L165 440 Z" fill="#0a0518" stroke="rgba(192,132,252,0.6)" strokeWidth="1.5" />
          {/* neon seams */}
          <path d="M130 400 L130 500" stroke="#A855F7" strokeWidth="1.6" opacity="0.85" />
          <path d="M290 400 L290 500" stroke="#EC4899" strokeWidth="1.6" opacity="0.85" />
          {/* Chest arc reactor */}
          <g className="core-pulse">
            <circle cx="210" cy="440" r="16" fill="url(#coreGrad)" />
            <circle cx="210" cy="440" r="6" fill="#FDE68A" />
            <circle cx="210" cy="440" r="14" fill="none" stroke="#FDE68A" strokeWidth="0.8" opacity="0.6" />
          </g>
        </g>

        {/* Neck */}
        <rect x="195" y="345" width="30" height="35" rx="8" fill="url(#skin)" />

        {/* Face */}
        <ellipse cx="210" cy="255" rx="98" ry="118" fill="url(#skin)" stroke="rgba(0,0,0,0.12)" strokeWidth="1" />

        {/* Hair back */}
        <path d="M112 232 Q100 100 210 92 Q325 100 310 250 Q298 200 275 185 Q255 175 210 175 Q165 175 145 185 Q120 200 112 232 Z" fill="url(#hairGrad)" />

        {/* Hair front spikes */}
        <path d="M128 170 Q170 112 218 138 Q252 108 300 165 Q258 138 220 158 Q180 138 128 170 Z" fill="url(#hairGrad)" />
        <path d="M148 180 Q188 138 226 158" stroke="url(#hairHi)" strokeWidth="3.5" fill="none" strokeLinecap="round" filter="url(#glow)" />

        {/* Ears + headphone */}
        <ellipse cx="115" cy="260" rx="12" ry="20" fill="url(#skin)" />
        <ellipse cx="305" cy="260" rx="12" ry="20" fill="url(#skin)" />
        {/* Full headphone band */}
        <path d="M115 200 Q210 130 305 200" stroke="#A855F7" strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d="M115 200 Q210 130 305 200" stroke="#F0ABFC" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        {/* Ear cups */}
        <ellipse cx="102" cy="262" rx="18" ry="24" fill="#1a0d33" stroke="#A855F7" strokeWidth="1.5" />
        <ellipse cx="318" cy="262" rx="18" ry="24" fill="#1a0d33" stroke="#A855F7" strokeWidth="1.5" />
        <circle cx="102" cy="262" r="6" fill="#EC4899" filter="url(#glow)" />
        <circle cx="318" cy="262" r="6" fill="#EC4899" filter="url(#glow)" />

        {/* Eyebrows */}
        <path d="M165 218 Q180 208 200 218" stroke="#170b28" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M220 218 Q240 208 255 218" stroke="#170b28" strokeWidth="4" fill="none" strokeLinecap="round" />

        {/* Big anime eyes */}
        <g className="anime-eye">
          <ellipse cx="178" cy="258" rx="18" ry="22" fill="#0f0522" />
          <ellipse cx="180" cy="255" rx="12" ry="16" fill="url(#visorGrad)" />
          <circle cx="176" cy="252" r="4" fill="#ffffff" />
          <circle cx="184" cy="262" r="2" fill="#ffffff" opacity="0.7" />

          <ellipse cx="242" cy="258" rx="18" ry="22" fill="#0f0522" />
          <ellipse cx="244" cy="255" rx="12" ry="16" fill="url(#visorGrad)" />
          <circle cx="240" cy="252" r="4" fill="#ffffff" />
          <circle cx="248" cy="262" r="2" fill="#ffffff" opacity="0.7" />
        </g>

        {/* Nose */}
        <path d="M210 275 Q208 292 216 300" stroke="rgba(0,0,0,0.25)" strokeWidth="1.6" fill="none" strokeLinecap="round" />

        {/* Smile */}
        <path d="M188 318 Q210 332 232 318" stroke="#3a1720" strokeWidth="2.6" fill="none" strokeLinecap="round" />

        {/* Cheek */}
        <ellipse cx="155" cy="300" rx="11" ry="4.5" fill="#f8a8a0" opacity="0.5" />
        <ellipse cx="265" cy="300" rx="11" ry="4.5" fill="#f8a8a0" opacity="0.5" />

        {/* Circuit accents */}
        <g opacity="0.75" stroke="#C084FC" strokeWidth="1.1">
          <path d="M175 440 L175 465 L192 465" fill="none" />
          <path d="M228 465 L245 465 L245 440" fill="none" />
          <circle cx="175" cy="438" r="2.2" fill="#FDE68A" />
          <circle cx="245" cy="438" r="2.2" fill="#EC4899" />
        </g>

        {/* Data particles */}
        <g fill="#C084FC" opacity="0.9">
          <circle cx="60" cy="130" r="2.5" />
          <circle cx="370" cy="100" r="2" />
          <circle cx="350" cy="340" r="2.5" />
          <circle cx="55" cy="340" r="2" />
        </g>
        <g fill="#FDE68A" opacity="0.85">
          <circle cx="30" cy="230" r="1.8" />
          <circle cx="395" cy="215" r="1.8" />
        </g>
      </svg>
    </div>
  );
};

export default AnimeCharacter;